import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from "react"
import type { ReactNode } from "react"
import { QuestionReads } from "./QuestionReads"
import { useReducedMotion, useTypedPlaceholder } from "./useTypedPlaceholder"

export const ACCEPT = ".pdf,.docx,.xlsx,.png,.jpg,.jpeg,.txt,.md"
const PLACEHOLDER = "Describe your situation, or drop a notice here."

export interface ComposerHandle { focus: () => void; select: (caret: number) => void }

interface ComposerProps {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  onFiles: (files: File[]) => void
  /** Blocks submit while an upload is being created. */
  busy?: boolean
  /** Short status line under the hint (aria-live). */
  note?: string
  /** What is left and any limit line, under the hint (the allowance hint, a reset line). */
  hint?: ReactNode
  /** Attachment tray, rendered under the box. */
  tray?: ReactNode
  /** Called when the reader types over an auto-typed starter. */
  onUserInput?: () => void
  /** Draft check: the box reads "paste your draft" and a tag says so. */
  draft?: boolean
  onLeaveDraft?: () => void
  /** Instead of the file picker (the public site on its own origin sends the reader to the notice door). */
  onAttach?: () => void
  /** Show what Nira reads in the question as it is typed (sections, years, rupees, the State). */
  reads?: boolean
  /** Example questions typed out, one after another, in the empty and unfocused box. Off when omitted. */
  typed?: readonly string[]
}

const Clip = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M21 11.5 12.5 20a5 5 0 0 1-7-7l8.5-8.5a3.3 3.3 0 0 1 4.7 4.7L10.3 17.6a1.6 1.6 0 0 1-2.3-2.3L16 7.3" />
  </svg>
)
const Arrow = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 19V5M6 11l6-6 6 6" />
  </svg>
)

/** Home's composer (Composer README): one field, a quiet paper clip, a cobalt arrow, and a hint under it. */
export const Composer = forwardRef<ComposerHandle, ComposerProps>(function Composer(
  { value, onChange, onSubmit, onFiles, busy, note, hint, tray, onUserInput, draft, onLeaveDraft, onAttach, reads, typed },
  handle,
) {
  const taRef = useRef<HTMLTextAreaElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  useImperativeHandle(handle, () => ({
    focus: () => taRef.current?.focus(),
    select: (caret) => taRef.current?.setSelectionRange(caret, caret),
  }))

  const [focused, setFocused] = useState(false)
  const reduced = useReducedMotion()
  const animate = !!typed?.length && !reduced && !draft
  const overlay = animate && !focused && value.length === 0
  const ghost = useTypedPlaceholder(typed ?? [], overlay)

  const typing = value.length > 0
  const hasText = value.trim().length > 0

  /* ---- autosize ---- */
  const autosize = () => {
    const ta = taRef.current
    if (!ta) return
    const mh = parseFloat(getComputedStyle(ta).maxHeight) || 360
    ta.style.height = "auto"
    ta.style.height = `${Math.min(ta.scrollHeight, mh)}px`
  }
  useLayoutEffect(autosize, [value, draft])
  // A box that opens with text in it is measured again once the serif has loaded: measured in the
  // fallback font, it is a line short and shows a scrollbar.
  useEffect(() => {
    let live = true
    document.fonts?.ready.then(() => { if (live) autosize() }).catch(() => undefined)
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* ---- is-typing on <body> while there is text ---- */
  useEffect(() => {
    document.body.classList.toggle("is-typing", typing)
  }, [typing])
  useEffect(() => () => {
    document.body.classList.remove("is-typing")
  }, [])

  return (
    <div className="nl-sz">
      <div className={`nl-lbox${draft ? " draft" : ""}${overlay ? " typed" : ""}`} onPointerDown={(e) => { if (e.target === e.currentTarget) { e.preventDefault(); taRef.current?.focus() } }}>
        <textarea
          ref={taRef}
          id="nl-q"
          className="nl-q"
          rows={3}
          aria-label="Ask a GST question"
          spellCheck
          placeholder={draft ? "Paste your draft here, or attach it." : PLACEHOLDER}
          value={value}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={(e) => { onUserInput?.(); onChange(e.target.value) }}
          onKeyDown={(e) => {
            onUserInput?.()
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); onSubmit() }
          }}
        />
        {overlay ? (
          <span className="nl-ph" aria-hidden="true" data-testid="typed-placeholder">{ghost}<i className="nl-caret" /></span>
        ) : null}
        {draft ? (
          <button className="nl-tag" type="button" aria-label="Stop checking a draft" onClick={onLeaveDraft}>Checking a draft ×</button>
        ) : null}
        <div className="nl-tools">
          <input
            ref={fileRef}
            type="file"
            multiple
            hidden
            accept={ACCEPT}
            data-testid="file-input"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? [])
              e.target.value = ""
              if (files.length) onFiles(files)
            }}
          />
          <button className="nl-attach" type="button" aria-label="Attach a notice or draft" onClick={() => (onAttach ? onAttach() : fileRef.current?.click())}>
            {Clip}
          </button>
          <button
            className={`nl-send${hasText && !busy ? " has" : ""}`}
            type="button"
            aria-label="Ask"
            aria-disabled={!hasText || !!busy}
            onClick={onSubmit}
          >
            {Arrow}
          </button>
        </div>
      </div>
      {reads && !draft ? <QuestionReads text={value} /> : null}
      {tray}
      <p className="nl-help">
        <span>Attach a notice or draft · PDF, Word, or a phone photo</span>
        <span>Enter to ask, Shift+Enter for a new line</span>
      </p>
      {hint}
      <p className={`nl-note${note ? " show" : ""}`} role="status">{note}</p>
    </div>
  )
})
